import type { EChartsOption } from 'echarts';
import type { ChartOptionBuildOptions } from './chart-option-chrome';
import {
  buildAxisCommon,
  buildSharedChrome,
  buildStableGrid,
  buildTooltipStyle,
  pickColor,
} from './chart-option-chrome';
import { formatChartTooltipValue } from './format-chart-tooltip-value';
import { resolveEchartsThemeTokens } from './resolve-echarts-theme-tokens';

/** Stable series id shared by map / bar / pie bags so `universalTransition` can morph. */
export const MAP_BAR_MORPH_SERIES_ID = 'factories-by-emirate';

/** Roam floor / ceiling so the UAE outline cannot collapse or over-zoom. */
export const MAP_BAR_MORPH_SCALE_LIMIT = { min: 1, max: 4 } as const;

/** View mode for {@link createMapBarMorphOptions}. */
export type MapBarMorphMode = 'bar' | 'pie' | 'map';

/** One named value (emirate label + factory count). */
export type MapBarMorphDatum = {
  name: string;
  value: number;
};

/**
 * Input for {@link createMapBarMorphOptions}.
 *
 * @example
 * createMapBarMorphOptions({
 *   data: [{ name: 'Dubai', value: 80 }],
 *   mapName: 'uae-emirates',
 *   mode: 'map',
 * });
 */
export type MapBarMorphInput = {
  data: readonly MapBarMorphDatum[];
  /** Name passed to `echarts.registerMap` / `series.map`. Unused for pie. */
  mapName: string;
  mode: MapBarMorphMode;
};

const ANIMATION_DURATION_UPDATE = 1000;

function formatItemTooltip(params: unknown): string {
  if (typeof params !== 'object' || params === null) {
    return '';
  }
  const name = 'name' in params ? String(params.name) : '';
  const rawValue = 'value' in params ? params.value : '';
  const value =
    typeof rawValue === 'number' || typeof rawValue === 'string'
      ? formatChartTooltipValue(rawValue)
      : '';
  return name ? `${name}<br/>${value}` : value;
}

/**
 * Builds an ECharts option bag for the map ↔ bar ↔ pie morph card.
 *
 * All modes share {@link MAP_BAR_MORPH_SERIES_ID} and `universalTransition: true`.
 * Callers must apply the next bag with `notMerge: true` (see
 * `ChartRendererComponent.replaceOption`) so leftover axes / visualMap do not
 * survive the toggle.
 *
 * @param input - Mode, registered map name, and named values.
 * @param buildOptions - Optional theme override.
 * @returns A bare {@link EChartsOption}.
 */
export function createMapBarMorphOptions(
  input: MapBarMorphInput,
  buildOptions: ChartOptionBuildOptions = {}
): EChartsOption {
  const theme = buildOptions.theme ?? resolveEchartsThemeTokens();
  const chrome = buildSharedChrome(theme, buildOptions.showToolbox ?? false);
  const maxValue = input.data.reduce(
    (max, item) => Math.max(max, item.value),
    0
  );

  if (input.mode === 'map') {
    const [emptyAreaColor] = theme.choroplethRamp;
    const hoverAreaColor =
      theme.choroplethRamp.at(-2) ?? theme.choroplethRamp.at(-1);
    return {
      ...chrome,
      animationDurationUpdate: ANIMATION_DURATION_UPDATE,
      tooltip: {
        ...buildTooltipStyle(theme),
        trigger: 'item',
        formatter: formatItemTooltip,
      },
      visualMap: {
        calculable: true,
        inRange: {
          color: [...theme.choroplethRamp],
        },
        left: 'right',
        max: Math.max(maxValue, 1),
        min: 0,
        text: ['High', 'Low'],
        textStyle: { color: theme.mutedColor },
      },
      series: [
        {
          animationDurationUpdate: ANIMATION_DURATION_UPDATE,
          data: input.data.map((item) => ({
            name: item.name,
            value: item.value,
          })),
          emphasis: {
            itemStyle: {
              areaColor: hoverAreaColor,
              borderColor: theme.textColor,
              borderWidth: 1.5,
            },
            label: { color: theme.textColor, show: true },
          },
          id: MAP_BAR_MORPH_SERIES_ID,
          itemStyle: {
            areaColor: emptyAreaColor,
            borderColor: theme.textColor,
            borderWidth: 1,
          },
          label: {
            color: theme.textColor,
            // Hidden until hover — northern emirates overlap at country scale.
            show: false,
          },
          map: input.mapName,
          roam: true,
          scaleLimit: { ...MAP_BAR_MORPH_SCALE_LIMIT },
          // Default true: a colored circle at each region's label centroid.
          showLegendSymbol: false,
          // Click-to-select highlight — not used on this observatory card.
          selectedMode: false,
          type: 'map',
          universalTransition: true,
        },
      ],
    };
  }

  if (input.mode === 'pie') {
    const { categoricalPalette } = theme;
    return {
      ...chrome,
      animationDurationUpdate: ANIMATION_DURATION_UPDATE,
      legend: {
        show: true,
        bottom: 0,
        textStyle: { color: theme.mutedColor },
      },
      tooltip: {
        ...buildTooltipStyle(theme),
        trigger: 'item',
        formatter: formatItemTooltip,
      },
      series: [
        {
          animationDurationUpdate: ANIMATION_DURATION_UPDATE,
          center: ['50%', '44%'],
          data: input.data.map((item, index) => ({
            itemStyle: { color: pickColor(categoricalPalette, index) },
            name: item.name,
            value: item.value,
          })),
          emphasis: {
            scale: true,
            itemStyle: { shadowBlur: 10, shadowOffsetX: 0 },
          },
          id: MAP_BAR_MORPH_SERIES_ID,
          radius: '58%',
          selectedMode: false,
          type: 'pie',
          universalTransition: true,
        },
      ],
    };
  }

  const axis = buildAxisCommon(theme);
  // Densest-first top (category axis draws index 0 at the bottom).
  const barData = [...input.data].sort((a, b) => a.value - b.value);

  return {
    ...chrome,
    animationDurationUpdate: ANIMATION_DURATION_UPDATE,
    grid: buildStableGrid({ left: 96, right: 24, top: 24, bottom: 40 }),
    tooltip: {
      ...buildTooltipStyle(theme),
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: unknown): string => {
        const items: unknown[] = Array.isArray(params) ? params : [params];
        const [first] = items;
        if (typeof first !== 'object' || first === null) {
          return '';
        }
        const name = 'name' in first ? String(first.name) : '';
        const rawValue = 'value' in first ? first.value : '';
        const value =
          typeof rawValue === 'number' || typeof rawValue === 'string'
            ? formatChartTooltipValue(rawValue)
            : '';
        return name ? `${name}<br/>${value}` : value;
      },
    },
    xAxis: {
      ...axis,
      type: 'value',
      minInterval: 1,
    },
    yAxis: {
      ...axis,
      type: 'category',
      data: barData.map((item) => item.name),
      axisLabel: {
        ...axis.axisLabel,
        rotate: 0,
        overflow: 'truncate',
        width: 80,
      },
    },
    series: [
      {
        animationDurationUpdate: ANIMATION_DURATION_UPDATE,
        data: barData.map((item) => ({
          name: item.name,
          value: item.value,
        })),
        id: MAP_BAR_MORPH_SERIES_ID,
        type: 'bar',
        universalTransition: true,
      },
    ],
  };
}
